import React, { useState } from 'react';
import type { Modification } from '../types/modification';
import { categories } from '../utils/categories';
import { VendorCombobox } from './VendorCombobox';
import { CustomDatePicker } from './CustomDatePicker';
import CustomDropdown from './CustomDropdown';
import { specificationsForStorage } from '../utils/specifications';
import { ModPhotoField } from './vehicle-detail/ModPhotoField';
import Modal from './ui/Modal';
import ConfirmDialog from './ui/ConfirmDialog';
import { useEditorDraft, useEditorBack } from '../hooks/useEditorDraft';

interface Vendor { name: string; }
interface AddModificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (modification: Omit<Modification, 'id'>, photoFile?: File | null) => Promise<void>;
  existingModifications: Modification[];
  vendors: Vendor[];
  refreshVendors: () => Promise<void>;
  /** Prefill parent when adding a part inside an assembly */
  buildLabel?: string;
  draftKey?: string;
  initialParent?: { id?: string; title: string; category?: string } | null;
}

export const AddModificationModal: React.FC<AddModificationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  existingModifications,
  vendors,
  refreshVendors,
  initialParent = null,
  buildLabel,
  draftKey,
}) => {
  const emptyForm = React.useMemo(() => ({
    title: '', category: initialParent?.category || '', description: '', cost: '',
    installDate: '', vendor: '', partNumber: '', notes: '', specifications: '',
    parentComponent: initialParent?.title || '', parentModificationId: initialParent?.id || '',
  }), [initialParent?.category, initialParent?.title, initialParent?.id]);
  const [formData, setFormData, clearDraft] = useEditorDraft(draftKey, emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const savingRef = React.useRef(false);
  const addAnotherRef = React.useRef(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const formRef = React.useRef<HTMLFormElement>(null);
  const dirty = JSON.stringify(formData) !== JSON.stringify(emptyForm) || Boolean(photoFile);
  const requestClose = () => {
    if (savingRef.current) return;
    if (dirty) setConfirmDiscard(true);
    else onClose();
  };
  useEditorBack(isOpen, requestClose, dirty || isSubmitting);

  const categoryNames = Array.from(new Set([...categories.map(category => category.name),
    ...existingModifications.map(part => part.category), formData.category].filter(Boolean)));
  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (savingRef.current) return;
    const addAnother = (e.nativeEvent as SubmitEvent).submitter?.getAttribute('data-add-another') === 'true' || addAnotherRef.current;
    addAnotherRef.current = false;

    // Validation
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Enter a part name';
    }

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }

    if (formData.cost && (!Number.isFinite(Number(formData.cost)) || Number(formData.cost) < 0)) {
      newErrors.cost = 'Enter a cost of zero or more';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length) {
      window.requestAnimationFrame(() => formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus());
    }

    if (Object.keys(newErrors).length === 0) {
      const specLines = specificationsForStorage(formData.specifications);

      // Build with camelCase for TS
      const modification: Omit<Modification, 'id'> = {
        title: formData.title.trim(),
        category: formData.category.trim(),
        description: formData.description.trim() || undefined,
        cost: formData.parentComponent.trim() || formData.parentModificationId
          ? 0
          : formData.cost
            ? Number(formData.cost)
            : undefined,
        installDate: formData.installDate || undefined,
        vendor: formData.vendor.trim() || undefined,
        partNumber: formData.partNumber.trim() || undefined,
        notes: formData.notes.trim() || undefined,
        parentComponent: formData.parentComponent.trim() || undefined,
        specifications: specLines.length > 0 ? specLines : undefined
      };

      // Map camelCase to snake_case for backend (use 'any' to avoid TS errors)
      const backendModification: any = {
        ...modification,
        install_date: modification.installDate,
        part_number: modification.partNumber,
      };
      if (formData.parentModificationId) {
        backendModification.parent_modification_id = parseInt(formData.parentModificationId, 10);
      }
      delete backendModification.installDate;
      delete backendModification.partNumber;

      savingRef.current = true;
      setIsSubmitting(true);
      setSaveError(null);
      try {
        await onSubmit(backendModification, photoFile);
        await refreshVendors().catch(() => undefined);

        const next = { ...emptyForm, category: formData.category,
          parentComponent: formData.parentComponent, parentModificationId: formData.parentModificationId };
        clearDraft();
        setFormData(addAnother ? next : emptyForm);
        setPhotoFile(null);
        setErrors({});
        if (addAnother) {
          setSavedMessage(`Saved ${formData.title.trim()}. Ready for the next part.`);
          formRef.current?.closest('[role="dialog"]')?.querySelector('input')?.focus();
        } else onClose();
      } catch (error) {
        setSaveError(error instanceof Error ? error.message : 'Could not add the modification. Please try again.');
      } finally {
        savingRef.current = false;
        setIsSubmitting(false);
      }
    }
  };

  const updateField = (field: string, value: string) => {
    setSavedMessage('');
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'parentComponent') setFormData(prev => ({ ...prev, parentModificationId: existingModifications.find(mod => mod.title === value)?.id || '' }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <>
    <Modal isOpen={isOpen} onClose={requestClose} title={initialParent?.title ? 'Add part inside' : 'Add part'}
      description={buildLabel ? `${buildLabel}${initialParent?.title ? ` · ${initialParent.title}` : ''}` : 'Part name and category are all you need.'}
      size="lg" mobileFullscreen initialFocus="dialog" className="part-editor p-4 sm:p-6"
      closeOnEscape={!isSubmitting} closeOnOverlayClick={!isSubmitting}
      footer={<div className="space-y-2">
        <p role="status" className={`text-xs ${saveError ? 'text-red-300' : 'text-gray-400'}`}>
          {isSubmitting ? 'Saving…' : saveError ? 'Not saved. Your entries are kept here.' : savedMessage || (dirty ? 'Unsaved part' : 'Ready to add a part')}
        </p>
        <div className="flex gap-2">
          <button type="submit" form="add-part-form" data-testid="mod-submit" disabled={isSubmitting} className="btn-primary flex-1 justify-center min-h-[44px]">{isSubmitting ? 'Saving…' : 'Save part'}</button>
          <button type="submit" form="add-part-form" data-add-another="true" onClick={() => { addAnotherRef.current = true; }} disabled={isSubmitting} className="btn-secondary flex-1 justify-center min-h-[44px]">Save + another</button>
        </div>
        <button type="button" onClick={requestClose} disabled={isSubmitting} className="btn-ghost w-full min-h-[44px] justify-center">Cancel</button>
      </div>}>
        <form id="add-part-form" ref={formRef} onSubmit={handleSubmit} className="min-w-0 space-y-5" noValidate>
          {saveError && <p role="alert" className="text-red-300">{saveError}</p>}
          <fieldset disabled={isSubmitting} className="min-w-0 space-y-5">
          {/* Required Fields */}
          <div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="new-part-name" className="input-label">
                  Part name *
                </label>
                <input
                  type="text" id="new-part-name" required aria-invalid={!!errors.title} aria-describedby={errors.title ? 'new-part-name-error' : undefined}
                  data-testid="mod-title"
                  value={formData.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  className={`input ${errors.title ? 'border-red-500' : ''}`}
                  placeholder="e.g., Garrett GT2860RS Turbocharger"
                />
                {errors.title && <p id="new-part-name-error" className="input-error">{errors.title}</p>}
              </div>

              <div>
                <label htmlFor="new-part-category" className="input-label">
                  Category *
                </label>
                <select id="new-part-category" value={formData.category} onChange={e => updateField('category', e.target.value)}
                  className="input" required aria-invalid={!!errors.category} aria-describedby={errors.category ? 'new-part-category-error' : undefined} data-testid="mod-category">
                  <option value="">Select a category</option>
                  {categoryNames.map(name => <option key={name} value={name}>{name}</option>)}
                </select>
                {errors.category && <p id="new-part-category-error" className="input-error">{errors.category}</p>}
              </div>
            </div>
          </div>

          {/* Optional Fields */}
          <details className="part-editor-details">
            <summary className="input-label min-h-[44px] flex items-center cursor-pointer">Optional details <span aria-hidden="true" className="ml-auto mr-2">+</span><span className="text-muted text-xs ml-2">Cost, brand, photo and more</span></summary>
            <div className="space-y-4">
              <div>
                <label htmlFor="new-part-description" className="input-label">
                  Description
                </label>
                <textarea
                  id="new-part-description" value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  rows={3}
                  className="input"
                  placeholder="Brief description of the modification..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="new-part-cost" className="input-label">
                    Cost ($)
                  </label>
                  <input
                    type="number" inputMode="decimal" aria-invalid={!!errors.cost}
                    step="0.01"
                    min="0"
                    id="new-part-cost" value={formData.cost}
                    onChange={(e) => updateField('cost', e.target.value)}
                    className={`input ${errors.cost ? 'border-red-500' : ''}`}
                    placeholder="0.00"
                  />
                  {errors.cost && <p className="input-error">{errors.cost}</p>}
                </div>

                <div>
                  <CustomDatePicker
                    label="Install Date"
                    value={formData.installDate}
                    onChange={(date) => updateField('installDate', date)}
                    placeholder="Select install date..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="input-label">
                    Vendor
                  </label>
                  <VendorCombobox
                    id="part-vendor"
                    value={formData.vendor}
                    onChange={(value) => updateField('vendor', value)}
                    placeholder="Search or add vendor..."
                    vendors={vendors.map(v => v.name)}
                  />
                </div>

                <div>
                  <label htmlFor="new-part-partNumber" className="input-label">
                    Part Number
                  </label>
                  <input
                    type="text"
                    id="new-part-partNumber" value={formData.partNumber}
                    onChange={(e) => updateField('partNumber', e.target.value)}
                    className="input"
                    placeholder="e.g., 466541-5004S"
                  />
                </div>
              </div>

              <div>
                <label className="input-label">
                  Parent Component
                </label>
                <CustomDropdown
                  value={formData.parentComponent}
                  onChange={(value) => updateField('parentComponent', value)}
                  options={[
                    { value: '', label: 'Select parent component (optional)' },
                    ...existingModifications.map(mod => ({
                      value: mod.title,
                      label: mod.title
                    }))
                  ]}
                  placeholder="Select parent component (optional)"
                />
                <p className="input-helper">
                  Select if this modification is part of a larger component or system<br></br>
                  (If parent selected, will set cost to $0.00)
                </p>
              </div>

              <div>
                <label htmlFor="new-part-specifications" className="input-label">
                  Specifications
                </label>
                <textarea
                  id="new-part-specifications" value={formData.specifications}
                  onChange={(e) => updateField('specifications', e.target.value)}
                  rows={4}
                  className="input"
                  placeholder={'Final drive: 4.308\nRear differential: 3.309\nGear ratios: 3.000, 2.000, 1.471, 1.111, 0.857'}
                />
                <p className="input-helper">
                  Add one detail per line.
                </p>
              </div>

              <div>
                <ModPhotoField value={photoFile} onChange={setPhotoFile} />
              </div>

              <div>
                <label htmlFor="new-part-notes" className="input-label">
                  Notes
                </label>
                <textarea
                  id="new-part-notes" value={formData.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  rows={3}
                  className="input"
                  placeholder="Additional notes or comments..."
                />
              </div>
            </div>
          </details>
          </fieldset>
        </form>
    </Modal>
    <ConfirmDialog isOpen={confirmDiscard} title="Discard unsaved part?" description="Your part has not been saved. Keep editing to finish it."
      confirmLabel="Discard changes" cancelLabel="Keep editing" destructive
      onCancel={() => setConfirmDiscard(false)} onConfirm={() => { clearDraft(); onClose(); }} />
    </>
  );
};
